// App.jsx — root: global providers + error boundary + router + routes.
// Per-page chrome (header, footer) is rendered by each page; the cookie consent banner is
// mounted ONCE here (CookieConsentBanner — it defers to the static /cookie-consent.js banner
// when that one is already in the DOM and stamps the launch-contract markers on it).
//
// Two exports, one tree:
//   AppProviders — every provider except the router. Shared by the browser entry
//                  (main.jsx → <App/>) and the server entry (entry-server.jsx →
//                  <StaticRouter>) so the server HTML and the hydrating client render
//                  the SAME component tree. A mismatch makes React discard the server
//                  HTML and re-render on the client — the second, later LCP candidate
//                  SSR exists to remove (docs/SCALABILITY.md "SSR of the public routes").
//   App          — BrowserRouter around AppProviders + AppRoutes (browser only).
//
// Provider order matters:
//   Router (Browser/Static) — outermost: <Analytics /> (useLocation), UpgradeModal
//                             and every page link need a Router
//   ThemeProvider           — light | dark | system; owns <html data-theme> + .dark
//   ErrorBoundary           — catches render crashes below it
//   BrandProvider           — runtime brand tokens (primary/accent → CSS custom properties)
//   AuthProvider            — cookie-session auth (GET /api/sessions/me)
//   PermissionsProvider     — role permission set (GET /api/permissions/me), once per user
//   UpgradeProvider         — shared in-app upgrade modal (useUpgradeModal)
//   GlobalDateRangeProvider — shared reporting window (admin pages)
//
// History: commit de56e8c9 (2026-09-05) dropped the providers AND the router
// while reworking the layout; every product built from the template since then
// rendered only the ErrorBoundary fallback. Keep this file minimal, but never
// remove a provider that @system components depend on.

import { BrowserRouter } from "react-router-dom"
import { AppRoutes } from "./app/routes/@system/AppRoutes"
import { ErrorBoundary } from "./app/components/@system/ErrorBoundary"
import { HelpWidget } from "./app/components/@system/HelpWidget"
import { SkipToContent } from "./app/components/@system/SkipToContent"
import { CookieConsentBanner } from "./app/components/@system/CookieConsentBanner"
import { Toaster } from "./app/components/@custom/Toaster"
import Analytics from "./app/components/@system/Analytics"
import { ThemeProvider } from "./app/store/@system/theme"
import { BrandProvider } from "./app/store/@system/brand"
import { AuthProvider } from "./app/store/@system/auth"
import { PermissionsProvider } from "./app/hooks/@system/usePermissions"
import { UpgradeProvider } from "./app/store/@system/upgradeModal"
import { GlobalDateRangeProvider } from "./app/store/@system/dateRange"

/**
 * Every global provider except the router. Render it inside a Router
 * (BrowserRouter in the browser, StaticRouter on the server).
 * @param {{ children: import('react').ReactNode }} props
 */
export function AppProviders({ children }) {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <BrandProvider>
          <AuthProvider>
            <PermissionsProvider>
              <UpgradeProvider>
                <GlobalDateRangeProvider>
                  <SkipToContent />
                  <Analytics />
                  {children}
                  <CookieConsentBanner />
                  <Toaster />
                </GlobalDateRangeProvider>
              </UpgradeProvider>
            </PermissionsProvider>
          </AuthProvider>
        </BrandProvider>
      </ErrorBoundary>
      {/*
        Help → "Report a problem" (Orkosi lane [5721]): OUTSIDE the root ErrorBoundary and wrapped in its own,
        so a user whose page just crashed can still tell the app owner. Needs no router, auth or brand
        provider; its own boundary renders nothing when the widget itself fails.
      */}
      <ErrorBoundary fallback={() => null}>
        <HelpWidget />
      </ErrorBoundary>
    </ThemeProvider>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProviders>
        <AppRoutes />
      </AppProviders>
    </BrowserRouter>
  )
}
